import { z } from 'zod'

import { BillingAuthorizationTypeSchema } from './entity-billing-profile'
import { createPaginatedResponseSchema } from './pagination'
import { decimalSchema } from './prisma-zod'

export const BillingAuthorizationStatusSchema = z.enum([
  'DRAFT',
  'ACTIVE',
  'EXHAUSTED',
  'EXPIRED',
  'CANCELLED',
  'ARCHIVED',
])

export type BillingAuthorizationStatus = z.infer<
  typeof BillingAuthorizationStatusSchema
>

export const BillingAuthorizationStatusLabels: Record<
  BillingAuthorizationStatus,
  string
> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  EXHAUSTED: 'Exhausted',
  EXPIRED: 'Expired',
  CANCELLED: 'Canceled',
  ARCHIVED: 'Archived',
}

export const BillingAuthorizationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  billingProfileId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  authorizationType: BillingAuthorizationTypeSchema,
  referenceNumber: z.string().nullable().optional(),
  label: z.string(),
  status: BillingAuthorizationStatusSchema,
  authorizedAmount: decimalSchema.nullable().optional(),
  consumedAmount: decimalSchema.default(0),
  consumedTimeAmount: decimalSchema.default(0),
  consumedExpenseAmount: decimalSchema.default(0),
  importedConsumedAmount: decimalSchema.default(0),
  // Invoices generated from schedule lines (settings.source === 'billing_schedule')
  // that are past DRAFT; attributed directly to their linked authorization.
  consumedInvoiceAmount: decimalSchema.default(0),
  remainingAmount: decimalSchema.nullable().optional(),
  currency: z.string().default('USD'),
  effectiveDate: z.date().nullable().optional(),
  expirationDate: z.date().nullable().optional(),
  requiredOnInvoice: z.boolean().default(false),
  invoiceInstructions: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  documentUrl: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).default({}),
  // SD-125: accountable member (user id), org-scoped; null when unassigned.
  ownerId: z.string().uuid().nullable().optional(),
  // Read-only display fields resolved by the API (not accepted on writes).
  ownerName: z.string().nullable().optional(),
  entityName: z.string().nullable().optional(),
  projectName: z.string().nullable().optional(),
  contractTitle: z.string().nullable().optional(),
  // SD-122 forecast: sum of the live (not skipped/canceled) billing schedule
  // lines planned against this authorization. Null when none are linked.
  forecastAmount: decimalSchema.nullable().optional(),
  scheduledLineCount: z.number().int().nonnegative().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type BillingAuthorization = z.infer<typeof BillingAuthorizationSchema>

/** Consumption ratio (0..n) against the authorized amount; null when uncapped. */
export function getBillingAuthorizationUsageRatio(authorization: {
  authorizedAmount?: number | null
  consumedAmount?: number | null
}): number | null {
  const authorized = authorization.authorizedAmount
  if (authorized == null || !(authorized > 0)) return null
  return Number(authorization.consumedAmount ?? 0) / authorized
}

export interface BillingAuthorizationVariance {
  /** Authorized cap, or null when uncapped. */
  authorized: number | null
  /** Planned billing (schedule lines), or null when nothing is scheduled. */
  forecast: number | null
  /** Invoiced amount attributed to the authorization. */
  actualInvoiced: number
  /** All consumption (invoices, time, expenses, imported balance). */
  totalConsumed: number
  /** actualInvoiced - forecast; positive = invoiced over plan. */
  varianceToForecast: number | null
  /** varianceToForecast as a percent of forecast (null when forecast is 0/none). */
  varianceToForecastPercent: number | null
  /** authorized - totalConsumed (null when uncapped). */
  remaining: number | null
  /** remaining as a percent of authorized. */
  remainingPercent: number | null
  isOverForecast: boolean
  isOverAuthorized: boolean
}

const roundMoney = (value: number) => Math.round(value * 100) / 100

/**
 * Forecast vs. actual for one authorization (SD-122). "Actual" is the invoiced
 * amount; total consumption (which also counts time and expenses) is carried
 * alongside so surfaces can show both without re-deriving either.
 */
export function computeBillingAuthorizationVariance(authorization: {
  authorizedAmount?: number | null
  consumedAmount?: number | null
  consumedInvoiceAmount?: number | null
  forecastAmount?: number | null
}): BillingAuthorizationVariance {
  const authorized =
    authorization.authorizedAmount == null
      ? null
      : Number(authorization.authorizedAmount)
  const forecast =
    authorization.forecastAmount == null
      ? null
      : Number(authorization.forecastAmount)
  const actualInvoiced = Number(authorization.consumedInvoiceAmount ?? 0)
  const totalConsumed = Number(authorization.consumedAmount ?? 0)

  const varianceToForecast =
    forecast == null ? null : roundMoney(actualInvoiced - forecast)
  const varianceToForecastPercent =
    forecast != null && forecast > 0 && varianceToForecast != null
      ? Math.round((varianceToForecast / forecast) * 1000) / 10
      : null
  const remaining =
    authorized == null ? null : roundMoney(authorized - totalConsumed)
  const remainingPercent =
    authorized != null && authorized > 0 && remaining != null
      ? Math.round((remaining / authorized) * 1000) / 10
      : null

  return {
    authorized,
    forecast,
    actualInvoiced,
    totalConsumed,
    varianceToForecast,
    varianceToForecastPercent,
    remaining,
    remainingPercent,
    isOverForecast: varianceToForecast != null && varianceToForecast > 0,
    isOverAuthorized:
      authorized != null && authorized > 0 && totalConsumed > authorized,
  }
}

export const BillingAuthorizationsResponseSchema =
  createPaginatedResponseSchema(BillingAuthorizationSchema)

export type BillingAuthorizationsResponse = z.infer<
  typeof BillingAuthorizationsResponseSchema
>

export const UpsertBillingAuthorizationSchema = z.object({
  entityId: z.string().uuid(),
  billingProfileId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  authorizationType: BillingAuthorizationTypeSchema,
  referenceNumber: z.string().trim().nullable().optional(),
  label: z.string().trim().min(1),
  status: BillingAuthorizationStatusSchema.default('DRAFT'),
  authorizedAmount: decimalSchema.nullable().optional(),
  currency: z.string().trim().min(3).max(3).default('USD'),
  effectiveDate: z.coerce.date().nullable().optional(),
  expirationDate: z.coerce.date().nullable().optional(),
  requiredOnInvoice: z.boolean().default(false),
  invoiceInstructions: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  documentUrl: z
    .string()
    .trim()
    .url('Invalid document URL')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  metadata: z.record(z.unknown()).default({}),
  ownerId: z.string().uuid().nullable().optional(),
})

export type UpsertBillingAuthorization = z.infer<
  typeof UpsertBillingAuthorizationSchema
>

export const UpdateBillingAuthorizationSchema =
  UpsertBillingAuthorizationSchema.partial()

export type UpdateBillingAuthorization = z.infer<
  typeof UpdateBillingAuthorizationSchema
>

export const BillingAuthorizationSourceTypeSchema = z.enum([
  'time',
  'expense',
  'invoice',
])

export type BillingAuthorizationSourceType = z.infer<
  typeof BillingAuthorizationSourceTypeSchema
>

export const BillingAuthorizationSourceRowSchema = z.object({
  id: z.string().uuid(),
  type: BillingAuthorizationSourceTypeSchema,
  date: z.coerce.date(),
  memberName: z.string().nullable(),
  workItem: z.string().nullable(),
  /** Record the work item names, when it is one (time entry's work, invoice). */
  workItemModuleType: z.string().nullable().optional(),
  workItemModuleId: z.string().nullable().optional(),
  description: z.string().nullable(),
  billableAmount: decimalSchema,
  status: z.string(),
})

export type BillingAuthorizationSourceRow = z.infer<
  typeof BillingAuthorizationSourceRowSchema
>

export const BillingAuthorizationSourcesQuerySchema = z.object({
  type: BillingAuthorizationSourceTypeSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
})

export type BillingAuthorizationSourcesQuery = z.infer<
  typeof BillingAuthorizationSourcesQuerySchema
>

export const BillingAuthorizationSourcesResponseSchema =
  createPaginatedResponseSchema(BillingAuthorizationSourceRowSchema)

export type BillingAuthorizationSourcesResponse = z.infer<
  typeof BillingAuthorizationSourcesResponseSchema
>

export const EligibleBillingAuthorizationQuerySchema = z.object({
  entityId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  contractId: z.string().uuid().optional().nullable(),
  invoiceDate: z.coerce.date().optional(),
})

export type EligibleBillingAuthorizationQuery = z.infer<
  typeof EligibleBillingAuthorizationQuerySchema
>
